import moment from 'moment';
import { deepClone } from '../../../common/helpers';
import { defaultOption, otherOption } from '../components/InvoicingDelivery';

export const impressionMethod = 'impression';

export const scanMethod = 'scan';

// Transfer method: Dentist uses 3D scanner but sends files via external link
// Admin downloads files from external link and uploads to finalize the order
export const transferMethod = 'transfer';

export const mainComplaintsOptions = [
    'Protrusion',
    'Crowding',
    'Spacing',
    'Open bite',
    'Overbite',
    'Overjet',
    'Midline Shift',
    'Underbite',
    'Asymmetric Jaw',
    'Gummy Smile',
    'Crossbite',
    'Narrow Arch',
    'Class II div 1',
    'Class II div 2',
    'Class III',
];

export const emptyOrder = () => {
    const serialNumber = moment().format('YYMMDD-HHmmss');

    return {
        orderType: 'nova',
        practiceId: '',
        manufacturer: 'overseas-lab',
        prescriberName: '',
        patientName: '',
        patientGender: '',
        patientDOB: '',
        instructions: '',
        treatmentType: '',
        mainComplaints: [],
        arches: 'Upper and lower',
        allowIPR: '',
        allowAttachment: '',
        allowElastics: '',
        allowTADs: '',
        upperMidline: '',
        lowerMidline: '',
        attachmentTrayMaterial: 'Soft',
        trimLine: 'Straight',
        retainersRequired: 'Yes',
        retainersDelivered: 'No',
        serialNumber,
        method: '',
        invoicingOption: defaultOption,
        invoicingRecipient: '',
        deliveryOption: defaultOption,
        deliveryStreetAddress: '',
        deliverySuburb: '',
        deliveryPostcode: '',
        deliveryState: '',
    };
};

export const defaultFormStepsTitle = [
    'Case Attributes',
    'Case Design',
    'Submission Method',
    'Files Attachment',
    'Invoicing & Delivery',
    'Order Confirmation',
];

export const refinementFormStepsTitle = [
    'Case Design',
    '3D Scan / Impression',
    'Files Attachment',
    'Order Confirmation',
];

export const novaRefinementFormStepsTitle = [
    'Case Design',
    'Submission Method',
    'Files Attachment',
    'Order Confirmation',
];

export const initNovaRefinement = (sourceOrder) => {
    const base = emptyOrder();
    return {
        ...base,
        orderType: 'nova',
        practiceId: sourceOrder.practiceId,
        treatmentType: sourceOrder.treatmentType || '',
        // sourceArches is immutable — tracks the original arches from the source order
        sourceArches: sourceOrder.arches || 'Upper and lower',
        arches: sourceOrder.arches || 'Upper and lower',
        upperStepsCount: sourceOrder.upperStepsCount || 0,
        lowerStepsCount: sourceOrder.lowerStepsCount || 0,
        refinementIssueDescription: '',
        refinementProblemTeeth: [],
        lastUpperAlignerWorn: '',
        lastLowerAlignerWorn: '',
        includeLowerArchRetainer: '',
        includeUpperArchRetainer: '',
    };
};

/**
 * Hydrate the refinement form from an existing refinement order that needs to be finalised.
 * Unlike initNovaRefinement (which resets refinement-specific fields for a new submission),
 * this preserves all data already stored on the order so the admin can review it before
 * uploading the physical files.
 */
export const initFinalizeRefinement = (refinementOrder) => {
    const base = emptyOrder();
    return {
        ...base,
        ...refinementOrder,
        // sourceArches mirrors arches — the arch selection is already committed on the order
        sourceArches: refinementOrder.arches || 'Upper and lower',
    };
};

// ======================== Design Attributes ============================

const designAttributes = [
    {
        id: 'treatmentType',
        label: 'Treatment Type',
        items: ['5 to 5', '7 to 7'],
        required: true,
        scope: ['dental'],
        helperText: ['Smile Academy offers unlimited free refinements. (Terms & conditions apply)'],
    },
    {
        id: 'mainComplaints',
        label: 'Patient Concerns',
        type: 'multiselect',
        items: mainComplaintsOptions,
        required: true,
        scope: ['dental', 'ortho'],
        helperText: 'Select all applicable patient concerns (multiple selections allowed)',
    },
    {
        id: 'arches',
        label: 'Arches Selection',
        items: ['Upper and lower', 'Upper only', 'Lower only'],
        required: true,
        scope: ['dental', 'ortho'],
    },
    {
        id: 'allowIPR',
        label: 'Allow IPR',
        items: ['Yes', 'No'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'allowAttachment',
        label: 'Allow Attachment',
        items: ['Yes', 'No'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'allowElastics',
        label: 'Allow Elastics',
        items: ['Yes', 'No'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'allowTADs',
        label: 'Allow TADs (Temporary Anchorage Device)',
        items: ['Yes', 'No'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'upperMidline',
        label: 'Upper Midline',
        items: ['Maintain', 'Improve'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'lowerMidline',
        label: 'Lower Midline',
        items: ['Maintain', 'Improve'],
        required: true,
        scope: ['dental'],
    },
    {
        id: 'attachmentTrayMaterial',
        label: 'Attachment tray material preference',
        items: ['Soft', 'Hard'],
        scope: ['dental', 'ortho'],
        helperText: 'Soft material is recommended for easier insertion',
    },
    {
        id: 'trimLine',
        label: 'Trim Line',
        items: ['Straight', 'Scalloped'],
        scope: ['dental', 'ortho'],
        helperText: 'Straight trim line is recommended for better retention',
    },
];

/**
 * Restrict treatment type input field for refinement order. We can't downgrade a treatment type
 * @param {*} allAttributes a copy of all the design attributes
 * @param {*} order the current order being created
 * @param {*} allowFullArchTreatment does the practice allow full arch treatment
 */
const getDesignAttributesForRefinementOrder = (allAttributes, order, allowFullArchTreatment) => {
    // remove modifiers specific to full arch treatment if the treatment type is not full arch
    const isFullArchTreatment = order.treatmentType.match('7 to 7');
    let attributes = isFullArchTreatment
        ? allAttributes
        : allAttributes.filter((attr) => !attr.scope.includes('7to7-treatment'));

    attributes.forEach((attr) => {
        if (attr.id === 'treatmentType') {
            // remove 7 to 7 treatment option if the practice cannot do full arch treatment
            if (!allowFullArchTreatment) {
                attr.items = attr.items.filter((item) => !item.match('7 to 7'));
            }
        }
    });

    return attributes;
};

/**
 * Returns the relevant design attributes based on provided treatment plan and full arch treatment capability
 * @param {*} allAttributes a copy of all the design attributes
 * @param {*} order the current order being created
 * @param {*} allowFullArchTreatment does the practice allow full arch treatment
 */
const getDesignAttributesForOrder = (allAttributes, order, allowFullArchTreatment) => {
    // filter only dental attributes
    let attributes = allAttributes.filter((attr) => attr.scope.includes('dental'));

    // remove 7 to 7 treatment option if the practice cannot do full arch treatment
    if (!allowFullArchTreatment) {
        attributes = attributes.map((attr) => {
            if (attr.id === 'treatmentType' && attr.items.length === 2) attr.items.pop();
            return attr;
        });
    }

    // remove modifiers specific to full arch treatment if the treatment type is not full arch
    const isFullArchTreatment = order.treatmentType.match('7 to 7');
    if (!isFullArchTreatment) {
        attributes = attributes.filter((attr) => !attr.scope.includes('7to7-treatment'));
    }

    return attributes;
};

const applyArchesConditionalFields = (attributes, order) => {
    const arches = order.arches || '';
    return attributes.map((attr) => {
        if (attr.id === 'lowerMidline' && arches === 'Upper only') {
            return { ...attr, required: false, hidden: true };
        }
        if (attr.id === 'upperMidline' && arches === 'Lower only') {
            return { ...attr, required: false, hidden: true };
        }
        return attr;
    });
};

/**
 * Returns the input fields for the Case Design form
 * Filter based on whether the case is for a dentist or an ortho dentist
 * Display full arch treatment (7 to 7) only for allowed practices
 */
export const caseDesignAttributes = (order, allowFullArchTreatment = false) => {
    // clone with safely copies deeply nested objects/arrays
    const attributesCopy = deepClone(designAttributes);

    const attributes =
        order.refinement === 'Yes'
            ? getDesignAttributesForRefinementOrder(attributesCopy, order, allowFullArchTreatment)
            : getDesignAttributesForOrder(attributesCopy, order, allowFullArchTreatment);

    return applyArchesConditionalFields(attributes, order);
};

// ======================== Invoicing & Delivery ============================

export const invoicingRecipientAttributes = [
    {
        id: 'invoicingRecipient',
        label: 'Practice Name',
        required: true,
    },
];

export const deliveryAddressAttributes = [
    {
        id: 'deliveryStreetAddress',
        label: 'Street Address',
        required: true,
    },
    {
        id: 'deliverySuburb',
        label: 'Suburb',
        required: true,
    },
    {
        id: 'deliveryPostcode',
        label: 'Postcode',
        type: 'number',
        required: true,
    },
    {
        id: 'deliveryState',
        label: 'State/Country',
        required: true,
        items: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NZ'],
    },
];

// ======================== Order details ============================

/**
 * Hide these properties in the order details screen
 */
export const excludedProperties = ['fcOrderStatus', 'manufacturer', 'filename', 'invoicingOption', 'deliveryOption'];

// ======================== Helpers ============================

/**
 * Clears N/A values from the form values
 */
export const sanitizeFormEntry = (formValues, [key, value]) => {
    formValues[key] = value === 'N/A' ? '' : value;
    return formValues;
};

/**
 * Returns true if the the error fields contains errors
 */
export const hasMissingRequiredFields = (errorFields) => Object.values(errorFields).filter((x) => !!x).length !== 0;

/**
 * Finds the required fields that are missing
 */
export const findMissingRequiredFields = (formAttributes, order) => {
    const requiredFormAttributes = formAttributes.filter((el) => el.required);
    const requiredFieldErrors = requiredFormAttributes.reduce((errors, attr) => {
        if (attr.type === 'multiselect') {
            errors[attr.id] = !order[attr.id] || order[attr.id].length === 0;
        } else {
            errors[attr.id] = !order[attr.id];
        }
        return errors;
    }, {});
    return requiredFieldErrors;
};

export const formatOrderBeforeSubmission = (
    formValues,
    practice,
    photosUpload,
    maxillaryArchUpload,
    mandibularArchUpload
) => {
    const formattedForm = formatInvoicingAndDelivery(formValues, practice);
    const sanitizedFormValues = Object.entries(formattedForm).reduce(sanitizeFormEntry, {});
    if (sanitizedFormValues.upperMidline === '') delete sanitizedFormValues.upperMidline;
    if (sanitizedFormValues.lowerMidline === '') delete sanitizedFormValues.lowerMidline;
    return {
        ...sanitizedFormValues,
        photosUpload: photosUpload,
        maxillaryArchUpload: maxillaryArchUpload,
        mandibularArchUpload: mandibularArchUpload,
    };
};

const formatInvoicingAndDelivery = (order, practice) => {
    const orderCopy = { ...order };
    const isNewOrder = !order.orderId;
    const { deliveryStreetAddress, deliverySuburb, deliveryPostcode, deliveryState } = orderCopy;

    // Invoicing
    if (orderCopy.invoicingOption === defaultOption && isNewOrder) {
        orderCopy.invoicingRecipient = practice?.name;
    }
    delete orderCopy.invoicingOption;

    // delivery
    if (orderCopy.deliveryOption === defaultOption && isNewOrder) {
        orderCopy.deliveryAddress = practice?.fullAddress;
    } else if (orderCopy.deliveryOption === otherOption || isNewOrder) {
        orderCopy.deliveryAddress = `${deliveryStreetAddress}, ${deliverySuburb} ${deliveryPostcode} ${deliveryState}`;
    }

    ['deliveryOption', 'deliveryStreetAddress', 'deliverySuburb', 'deliveryPostcode', 'deliveryState'].forEach(
        (attr) => delete orderCopy[attr]
    );

    return orderCopy;
};
